/**
 * TenantSetupWizard — Feature-Centric Onboarding
 *
 * 9-step wizard that starts with feature selection (subgraphs), then lets
 * users enrich with additional nodes/edges, then configures team and funding,
 * and finally activates.
 *
 * Steps:
 * 1. Select Features — choose capabilities (subgraphs), optional product bundle
 * 2. Industry — multi-select NAICS sectors, pre-filled from product
 * 3. Location — state + city, pre-filled from product jurisdiction
 * 4. Regulatory Stack — mandatory RuleSets from product (DRR-090/096)
 * 5. Team & Roles — actor roles with qualification requirements
 * 6. Funding Sources — payers with RuleSet overlay impact (healthcare only)
 * 7. Services — specific services offered, pre-filled from product workflows
 * 8. Regulatory Profile — LLM-enhanced regulatory suggestions with constellation
 * 9. Review & Activate — summary + feature activation
 *
 * All steps after Features are optional enrichment — filling them out adds
 * more nodes and edges to the tenant's configuration graph. Skipping them
 * doesn't block activation.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Stepper,
  Button,
  Group,
  Container,
  Title,
  Text,
  Paper,
  Alert,
  Badge,
  Stack,
  ThemeIcon,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconApps,
  IconBuilding,
  IconMapPin,
  IconShieldCheck,
  IconUsers,
  IconCurrencyDollar,
  IconList,
  IconShield,
  IconRocket,
} from '@tabler/icons-react';

import { FeatureSelectionStep, type ProductDefinition } from './steps/FeatureSelectionStep';
import { IndustryStep } from './steps/IndustryStep';
import { LocationStep } from './steps/LocationStep';
import { RegulatoryStackStep } from './steps/RegulatoryStackStep';
import { TeamRolesStep, type TeamRoleConfig } from './steps/TeamRolesStep';
import { FundingSourcesStep } from './steps/FundingSourcesStep';
import { ServicesStep } from './steps/ServicesStep';
import { RegulatoryProfileStep } from './steps/RegulatoryProfileStep';
import { ActivateStep, type ReadinessGap } from './steps/ActivateStep';
import {
  useComposition,
  type Payer,
  type TenantMetadataInput,
} from './hooks/useComposition';
import type { RegulatoryNodeSuggestion } from './hooks/useRegulatoryOnboarding';
import { sectorHasPayers } from './data/naicsCodes';

// ── Product Activation Mutation (DRR-101) ──
const ACTIVATE_PRODUCT = gql`
  mutation ActivateProduct($tenantId: String!, $productId: String!) {
    activateProduct(tenantId: $tenantId, productId: $productId) {
      success
      productId
      overridesApplied
      workflowsActivated
      settingsApplied
      errors
    }
  }
`;

interface ActivateProductResult {
  success: boolean;
  productId: string;
  overridesApplied: number;
  workflowsActivated: number;
  settingsApplied: number;
  errors: string[];
}

// ── Tenant Onboarding Activation (DRR-073: Pending → Active) ──
const ACTIVATE_SYSTEM_TENANT = gql`
  mutation ActivateSystemTenant($tenantId: String!) {
    activateSystemTenant(tenantId: $tenantId) {
      tenantId
      onboardingStatus
    }
  }
`;

// ── Feature Activation Mutation ──
const ACTIVATE_FEATURES = gql`
  mutation ActivateFeatures($tenantId: String!, $featureIds: [String!]!, $productId: String) {
    activateFeatures(tenantId: $tenantId, featureIds: $featureIds, productId: $productId) {
      success
      activatedFeatures
      productApplied
      errors
    }
  }
`;

interface ActivateFeaturesResult {
  success: boolean;
  activatedFeatures: string[];
  productApplied: boolean;
  errors: string[];
}

// ── DRR-172: Regulatory Readiness Detection Query ──
const GET_ACTIVATION_READINESS_GAPS = gql`
  query GetActivationReadinessGaps($rulesetId: String!) {
    regulatoryReadinessGaps(rulesetId: $rulesetId) {
      gaps {
        ruleId
        ruleName
        missingConfigs {
          configType
          description
        }
      }
    }
  }
`;

interface ReadinessGapsQueryData {
  regulatoryReadinessGaps: {
    gaps: ReadinessGap[];
  };
}

export interface TenantSetupWizardProps {
  tenantId: string;
  onComplete?: (result: { workflowId: string; instanceId?: string }) => void;
  onCancel?: () => void;
}

interface WizardFormData {
  // Step 1: Feature selection (subgraphs) + optional product bundle
  selectedFeatureIds: string[];
  selectedProductId: string | null;
  product: ProductDefinition | null;

  // Step 2: Industry (multi-select, pre-filled from product)
  naicsSectors: string[];
  naicsSubsector?: string;

  // Step 3: Location (pre-filled from product jurisdiction)
  state?: string;
  city?: string;

  // Step 4: Regulatory acknowledgment
  acknowledgedRulesets: boolean;

  // Step 5: Team configuration
  teamRoles: TeamRoleConfig[];

  // Step 6: Funding sources
  payers: Payer[];

  // Step 7: Services
  services: string[];

  // Step 8: Regulatory profile (LLM suggestions)
  acceptedRegulatoryNodes: readonly RegulatoryNodeSuggestion[];
}

const INITIAL_FORM_DATA: WizardFormData = {
  selectedFeatureIds: [],
  selectedProductId: null,
  product: null,
  naicsSectors: [],
  naicsSubsector: undefined,
  state: undefined,
  city: undefined,
  acknowledgedRulesets: false,
  teamRoles: [],
  payers: [],
  services: [],
  acceptedRegulatoryNodes: [],
};

/**
 * Maps product industry identifiers to NAICS sector codes for pre-fill.
 */
const INDUSTRY_TO_NAICS: Record<string, string> = {
  assisted_living: '62',
  home_care: '62',
  dental: '62',
  outpatient_rehab: '62',
  hospital: '62',
  pharmacy: '62',
  mental_health: '62',
  finance: '52',
  insurance: '52',
  construction: '23',
  food_service: '72',
  accounting: '54',
  legal: '54',
};

// Healthcare industries that show the funding step
const HEALTHCARE_INDUSTRIES = new Set([
  'assisted_living', 'home_care', 'dental', 'outpatient_rehab',
  'hospital', 'pharmacy', 'mental_health',
]);

const ALL_STEPS = [
  { key: 'features', label: 'Features', description: 'Select capabilities', icon: IconApps },
  { key: 'industry', label: 'Industry', description: 'NAICS sectors', icon: IconBuilding },
  { key: 'location', label: 'Location', description: 'State & city', icon: IconMapPin },
  { key: 'regulatory-stack', label: 'Regulations', description: 'Required rules', icon: IconShieldCheck },
  { key: 'team', label: 'Team', description: 'Configure roles', icon: IconUsers },
  { key: 'funding', label: 'Funding', description: 'Payment sources', icon: IconCurrencyDollar },
  { key: 'services', label: 'Services', description: 'Offerings', icon: IconList },
  { key: 'regulatory-profile', label: 'Compliance', description: 'AI suggestions', icon: IconShield },
  { key: 'activate', label: 'Activate', description: 'Review & go', icon: IconRocket },
];

export function TenantSetupWizard({
  tenantId,
  onComplete,
  onCancel,
}: TenantSetupWizardProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<WizardFormData>(INITIAL_FORM_DATA);
  const [setupComplete, setSetupComplete] = useState(false);
  const [createdWorkflow, setCreatedWorkflow] = useState<{
    workflowId: string;
    instanceId?: string;
    taskCount: number;
  } | null>(null);
  const [activationResult, setActivationResult] = useState<ActivateProductResult | null>(null);

  const {
    createWorkflows,
    loading,
    error,
    clearError,
  } = useComposition();

  const [executeActivateProduct] = useMutation<{
    activateProduct: ActivateProductResult;
  }>(ACTIVATE_PRODUCT);

  const [executeActivateFeatures, { loading: activating }] = useMutation<{
    activateFeatures: ActivateFeaturesResult;
  }>(ACTIVATE_FEATURES);

  const [executeActivateSystemTenant] = useMutation<{
    activateSystemTenant: { tenantId: string; onboardingStatus: string };
  }>(ACTIVATE_SYSTEM_TENANT);

  // Determine if funding step should show — check features first, then product, then NAICS
  const isHealthcare = useMemo(() => {
    if (formData.selectedFeatureIds.includes('fhir')) return true;
    if (formData.product) return HEALTHCARE_INDUSTRIES.has(formData.product.industry);
    return formData.naicsSectors.some(sectorHasPayers);
  }, [formData.selectedFeatureIds, formData.product, formData.naicsSectors]);

  // Visible steps (filter out funding if not healthcare)
  const visibleSteps = useMemo(() => {
    if (!isHealthcare) {
      return ALL_STEPS.filter((s) => s.key !== 'funding');
    }
    return ALL_STEPS;
  }, [isHealthcare]);

  const totalSteps = visibleSteps.length;

  const getStepKey = useCallback(
    (index: number): string => visibleSteps[index]?.key ?? '',
    [visibleSteps]
  );

  // DRR-172: Query coverage gaps for each required ruleset when user reaches ActivateStep (step 9)
  const isOnActivateStep = getStepKey(activeStep) === 'activate';
  const primaryRulesetId = formData.product?.requiredRulesets[0] ?? '';
  const { data: readinessGapsData } = useQuery<ReadinessGapsQueryData>(
    GET_ACTIVATION_READINESS_GAPS,
    {
      variables: { rulesetId: primaryRulesetId },
      skip: !isOnActivateStep || !primaryRulesetId,
      fetchPolicy: 'cache-and-network',
    }
  );

  const readinessGaps: ReadinessGap[] = useMemo(() => {
    if (!readinessGapsData?.regulatoryReadinessGaps?.gaps) return [];
    return readinessGapsData.regulatoryReadinessGaps.gaps;
  }, [readinessGapsData]);

  const updateFormData = useCallback((updates: Partial<WizardFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const nextStep = useCallback(() => {
    setActiveStep((current) => Math.min(current + 1, totalSteps - 1));
  }, [totalSteps]);

  const prevStep = useCallback(() => {
    setActiveStep((current) => Math.max(current - 1, 0));
  }, []);

  // Handle product selection — pre-fill downstream fields + features
  const handleProductSelect = useCallback((product: ProductDefinition | null) => {
    if (!product) {
      setFormData((prev) => ({
        ...prev,
        selectedProductId: null,
        product: null,
      }));
      return;
    }

    // Pre-fill industry from product
    const naicsCode = INDUSTRY_TO_NAICS[product.industry];
    const preFillSectors = naicsCode ? [naicsCode] : [];

    setFormData((prev) => ({
      ...prev,
      selectedProductId: product.productId,
      product,
      // Pre-fill features from product subgraphs
      selectedFeatureIds: Array.from(new Set([...prev.selectedFeatureIds, ...product.subgraphs])),
      // Pre-fill industry (user can add more)
      naicsSectors: preFillSectors.length > 0 ? preFillSectors : prev.naicsSectors,
      // Pre-fill location from product jurisdiction
      state: product.jurisdiction || prev.state,
      // Reset downstream state that depends on product
      acknowledgedRulesets: false,
      teamRoles: [],
    }));
  }, []);

  // Build metadata for composition mutation
  const buildMetadata = useCallback((): TenantMetadataInput | null => {
    const product = formData.product;
    const primarySector = formData.naicsSectors[0];
    const state = formData.state;

    // Need at least features, a product, or an industry+state
    if (!product && formData.selectedFeatureIds.length === 0 && (!primarySector || !state)) {
      return null;
    }

    return {
      industry: primarySector ?? product?.industry ?? '',
      state: state ?? product?.jurisdiction ?? '',
      naicsCode: formData.naicsSubsector ?? primarySector ?? product?.industry,
      city: formData.city,
      payers: formData.payers.length > 0 ? formData.payers : undefined,
      services: formData.services.length > 0 ? formData.services : undefined,
      regulatoryNodes: formData.acceptedRegulatoryNodes.length > 0
        ? formData.acceptedRegulatoryNodes.map((n) => ({
            nodeId: n.nodeId,
            edgeType: n.edgeType,
          }))
        : undefined,
      customFields: product ? {
        productId: product.productId,
        productVersion: product.version,
        additionalNaicsSectors: formData.naicsSectors.slice(1),
        teamRoles: formData.teamRoles
          .filter((r) => r.count > 0)
          .map((r) => ({ roleId: r.roleId, roleName: r.roleName, count: r.count })),
        requiredRulesets: product.requiredRulesets,
        activatesWorkflows: product.activatesWorkflows,
        activatesSettings: product.activatesSettings,
        activatesOverrides: product.activatesOverrides,
        activatesConstellations: product.activatesConstellations,
      } : {
        additionalNaicsSectors: formData.naicsSectors.slice(1),
        teamRoles: formData.teamRoles
          .filter((r) => r.count > 0)
          .map((r) => ({ roleId: r.roleId, roleName: r.roleName, count: r.count })),
      },
    };
  }, [formData]);

  // Handle final activate — activateFeatures + createWorkflows (DRR-101)
  const handleActivate = useCallback(async () => {
    const metadata = buildMetadata();
    if (!metadata) return;

    clearError();

    // Activate features (subgraphs) + optional product overlay
    if (formData.selectedFeatureIds.length > 0 || formData.product) {
      try {
        await executeActivateFeatures({
          variables: {
            tenantId,
            featureIds: formData.selectedFeatureIds,
            productId: formData.product?.productId ?? null,
          },
        });
      } catch {
        // Feature activation is non-blocking — continue to workflow creation.
      }
    }

    // Also call legacy activateProduct if product selected (for full overlay)
    if (formData.product) {
      try {
        const { data } = await executeActivateProduct({
          variables: {
            tenantId,
            productId: formData.product.productId,
          },
        });
        if (data?.activateProduct) {
          setActivationResult(data.activateProduct);
        }
      } catch {
        // Product activation is non-blocking — workflows are already created.
      }
    }

    const result = await createWorkflows(tenantId, metadata);
    if (result?.success) {
      setCreatedWorkflow({
        workflowId: result.workflowId,
        instanceId: result.instanceId,
        taskCount: result.taskCount,
      });

      // DRR-073: Transition tenant onboarding_status Pending → Active
      try {
        await executeActivateSystemTenant({ variables: { tenantId } });
      } catch {
        // Non-blocking — tenant activation is best-effort from the wizard.
        // FERP Operations can also activate via TenantManagementPage.
      }

      setSetupComplete(true);
      onComplete?.({
        workflowId: result.workflowId,
        instanceId: result.instanceId,
      });
    }
  }, [tenantId, formData.selectedFeatureIds, formData.product, buildMetadata, createWorkflows, executeActivateFeatures, executeActivateProduct, executeActivateSystemTenant, clearError, onComplete]);

  // Step validation — features step is required; all others are optional enrichment
  const isStepValid = useCallback(
    (step: number): boolean => {
      const key = getStepKey(step);
      switch (key) {
        case 'features':
          return formData.selectedFeatureIds.length > 0 || formData.product !== null;
        case 'industry':
          return true; // Optional enrichment
        case 'location':
          return true; // Pre-filled from product, optional override
        case 'regulatory-stack':
          // Must acknowledge if product has rulesets
          return !formData.product?.requiredRulesets.length || formData.acknowledgedRulesets;
        case 'team':
          return true; // Optional
        case 'funding':
          return true; // Optional
        case 'services':
          return true; // Optional
        case 'regulatory-profile':
          return true; // Optional
        case 'activate':
          return true;
        default:
          return false;
      }
    },
    [formData, getStepKey]
  );

  // Completion screen
  if (setupComplete && createdWorkflow) {
    const product = formData.product;
    return (
      <Container size="sm" py="xl">
        <Paper p="xl" withBorder>
          <Group justify="center" mb="md">
            <ThemeIcon size={64} radius="xl" color="green" variant="light">
              <IconCheck size={36} />
            </ThemeIcon>
          </Group>
          <Title order={2} ta="center" mb="md">
            {product ? 'Product Activated' : 'Setup Complete'}
          </Title>
          <Text ta="center" c="dimmed" mb="lg">
            {product
              ? `${product.name} has been activated with ${formData.selectedFeatureIds.length} features.`
              : `${formData.selectedFeatureIds.length} feature${formData.selectedFeatureIds.length !== 1 ? 's' : ''} activated.`}
            {' '}Check the Approval Queue to review configurations.
          </Text>

          <Stack gap="xs" mb="xl">
            <Group justify="center" gap="xs">
              <Badge size="lg" variant="light" color="green">
                {createdWorkflow.taskCount} configurations provisioned
              </Badge>
              {formData.selectedFeatureIds.length > 0 && (
                <Badge size="lg" variant="light" color="blue">
                  {formData.selectedFeatureIds.length} features
                </Badge>
              )}
              {product && (
                <Badge size="lg" variant="light" color="violet">
                  {product.name} bundle
                </Badge>
              )}
              {activationResult && activationResult.overridesApplied > 0 && (
                <Badge size="lg" variant="light" color="violet">
                  {activationResult.overridesApplied} form customization{activationResult.overridesApplied !== 1 ? 's' : ''}
                </Badge>
              )}
              {formData.naicsSectors.length > 0 && (
                <Badge size="lg" variant="light" color="gray">
                  {formData.naicsSectors.length} industr{formData.naicsSectors.length !== 1 ? 'ies' : 'y'}
                </Badge>
              )}
            </Group>
          </Stack>

          <Group justify="center">
            <Button onClick={() => onComplete?.(createdWorkflow)} size="lg">
              Go to Dashboard
            </Button>
          </Group>
        </Paper>
      </Container>
    );
  }

  // Render current step content
  const renderStepContent = () => {
    const key = getStepKey(activeStep);

    switch (key) {
      case 'features':
        return (
          <FeatureSelectionStep
            selectedFeatureIds={formData.selectedFeatureIds}
            selectedProductId={formData.selectedProductId}
            onFeaturesChange={(ids) => updateFormData({ selectedFeatureIds: ids })}
            onProductSelect={handleProductSelect}
          />
        );

      case 'industry':
        return (
          <IndustryStep
            naicsSectors={formData.naicsSectors}
            naicsSubsector={formData.naicsSubsector}
            productIndustry={formData.product?.industry}
            productName={formData.product?.name}
            onSectorsChange={(sectors) => {
              updateFormData({
                naicsSectors: sectors,
                services: [], // Reset services when industry changes
              });
            }}
            onSubsectorChange={(subsector) => {
              updateFormData({ naicsSubsector: subsector ?? undefined });
            }}
          />
        );

      case 'location':
        return (
          <LocationStep
            state={formData.state}
            city={formData.city}
            onStateChange={(state) => updateFormData({ state })}
            onCityChange={(city) => updateFormData({ city })}
          />
        );

      case 'regulatory-stack':
        if (!formData.product) {
          return (
            <Alert icon={<IconShieldCheck size={16} />} color="blue" variant="light">
              Apply a product bundle in Step 1 to see regulatory requirements.
              You can still proceed — regulatory suggestions are available in the Compliance step.
            </Alert>
          );
        }
        return (
          <RegulatoryStackStep
            product={formData.product}
            acknowledged={formData.acknowledgedRulesets}
            onAcknowledge={(acknowledged) => updateFormData({ acknowledgedRulesets: acknowledged })}
          />
        );

      case 'team':
        if (!formData.product) {
          return (
            <Alert icon={<IconUsers size={16} />} color="blue" variant="light">
              Apply a product bundle in Step 1 to see role templates for your industry.
              You can configure team members after setup.
            </Alert>
          );
        }
        return (
          <TeamRolesStep
            product={formData.product}
            teamRoles={formData.teamRoles}
            onTeamRolesChange={(teamRoles) => updateFormData({ teamRoles })}
          />
        );

      case 'funding':
        if (!formData.product) {
          return (
            <Alert icon={<IconCurrencyDollar size={16} />} color="blue" variant="light">
              Apply a healthcare product bundle in Step 1 to configure funding sources.
            </Alert>
          );
        }
        return (
          <FundingSourcesStep
            product={formData.product}
            selectedPayers={formData.payers}
            onPayersChange={(payers) => updateFormData({ payers })}
          />
        );

      case 'services':
        return (
          <ServicesStep
            industry={formData.naicsSectors[0]}
            selected={formData.services}
            onChange={(services) => updateFormData({ services })}
          />
        );

      case 'regulatory-profile':
        return (
          <RegulatoryProfileStep
            naicsSector={formData.naicsSectors[0]}
            naicsSubsector={formData.naicsSubsector}
            state={formData.state}
            tenantId={tenantId}
            tenantName={tenantId}
            onAcceptedChange={(accepted) =>
              updateFormData({ acceptedRegulatoryNodes: accepted })
            }
          />
        );

      case 'activate':
        if (formData.product) {
          return (
            <ActivateStep
              product={formData.product}
              acknowledgedRulesets={formData.acknowledgedRulesets}
              teamRoles={formData.teamRoles}
              payers={formData.payers}
              readinessGaps={readinessGaps.length > 0 ? readinessGaps : undefined}
            />
          );
        }
        // Fallback summary for no-product path (features only)
        return (
          <Paper p="lg" withBorder>
            <Stack gap="md">
              <Group gap="xs">
                <IconRocket size={20} />
                <Text fw={600}>Review & Activate</Text>
              </Group>
              <Text size="sm" c="dimmed">
                Ready to activate {formData.selectedFeatureIds.length} feature{formData.selectedFeatureIds.length !== 1 ? 's' : ''} and generate workflows.
              </Text>
              {formData.selectedFeatureIds.length > 0 && (
                <Group gap="xs">
                  <Text size="sm" fw={500}>Features:</Text>
                  {formData.selectedFeatureIds.map((id) => (
                    <Badge key={id} size="sm" variant="light">{id}</Badge>
                  ))}
                </Group>
              )}
              {formData.naicsSectors.length > 0 && (
                <Group gap="xs">
                  <Text size="sm" fw={500}>Industries:</Text>
                  {formData.naicsSectors.map((s) => (
                    <Badge key={s} size="sm" variant="light">{s}</Badge>
                  ))}
                </Group>
              )}
              {formData.state && (
                <Text size="sm"><Text span fw={500}>Location:</Text> {formData.state}{formData.city ? `, ${formData.city}` : ''}</Text>
              )}
              {formData.payers.length > 0 && (
                <Text size="sm"><Text span fw={500}>Payers:</Text> {formData.payers.length} selected</Text>
              )}
              {formData.services.length > 0 && (
                <Text size="sm"><Text span fw={500}>Services:</Text> {formData.services.length} selected</Text>
              )}
              {formData.acceptedRegulatoryNodes.length > 0 && (
                <Text size="sm"><Text span fw={500}>Regulatory:</Text> {formData.acceptedRegulatoryNodes.length} nodes accepted</Text>
              )}
            </Stack>
          </Paper>
        );

      default:
        return null;
    }
  };

  const isLastStep = activeStep === totalSteps - 1;

  return (
    <Container size="lg" py="xl">
      <Title order={2} mb="xs">
        Set Up Your Business
      </Title>
      <Text size="sm" c="dimmed" mb="xl">
        Start by selecting features, then enrich your configuration with industry details,
        team roles, and compliance settings. All steps after Features are optional.
      </Text>

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          mb="md"
          onClose={clearError}
          withCloseButton
        >
          {error}
        </Alert>
      )}

      <Stepper
        active={activeStep}
        onStepClick={setActiveStep}
        mb="xl"
        size="sm"
      >
        {visibleSteps.map((step, index) => {
          const StepIcon = step.icon;
          return (
            <Stepper.Step
              key={step.key}
              label={step.label}
              description={step.description}
              icon={<StepIcon size={16} />}
              allowStepSelect={index <= activeStep}
            >
              {renderStepContent()}
            </Stepper.Step>
          );
        })}
      </Stepper>

      <Group justify="space-between" mt="xl">
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>

        <Group>
          {activeStep > 0 && (
            <Button variant="default" onClick={prevStep}>
              Back
            </Button>
          )}

          {isLastStep ? (
            <Button
              onClick={handleActivate}
              loading={loading || activating}
              leftSection={<IconRocket size={18} />}
              color="green"
            >
              {formData.product ? 'Activate Features & Product' : 'Activate Features'}
            </Button>
          ) : (
            <Button onClick={nextStep} disabled={!isStepValid(activeStep)}>
              Next
            </Button>
          )}
        </Group>
      </Group>
    </Container>
  );
}

export default TenantSetupWizard;
